import { useState, useEffect } from "react";
import { CheckCircle2, XCircle, Loader2, PartyPopper } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";

const LEVELS = ["PAUD/TK", "SD", "SMP", "SMA", "SMK", "Yayasan (Multi-unit)"];

const empty = {
  school_name: "", level: "", subdomain: "", student_count: "",
  contact_name: "", email: "", phone: "",
};

export const TrialDialog = ({ open, onOpenChange, presetPackage }) => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [check, setCheck] = useState(null); // {available, reason}
  const [checking, setChecking] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) {
      setForm(empty); setDone(null); setCheck(null);
    }
  }, [open]);

  useEffect(() => {
    const sub = form.subdomain.trim().toLowerCase();
    if (!sub) { setCheck(null); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/subdomain/check`, { params: { name: sub } });
        setCheck(data);
      } catch { setCheck(null); }
      setChecking(false);
    }, 450);
    return () => clearTimeout(t);
  }, [form.subdomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (check && !check.available) {
      toast.error("Subdomain belum tersedia. Silakan pilih nama lain.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        subdomain: form.subdomain.trim().toLowerCase(),
        student_count: form.student_count ? parseInt(form.student_count, 10) : null,
        package: presetPackage || null,
      };
      const { data } = await api.post(`/trial`, payload);
      setDone(data);
      toast.success("Pendaftaran uji coba berhasil dikirim!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gagal mengirim. Coba lagi.";
      toast.error(typeof msg === "string" ? msg : "Periksa kembali data Anda.");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-cyan-500/20 bg-[#0D1424] text-white sm:max-w-lg" data-testid="trial-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="trial-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
              <PartyPopper className="h-8 w-8" />
            </div>
            <h3 className="mt-5 font-heading text-2xl font-bold text-white">Selamat, {done.school_name}!</h3>
            <p className="mt-3 text-sm text-slate-300">
              Uji coba gratis 14 hari Anda sedang kami siapkan. Portal sekolah Anda akan aktif di:
            </p>
            <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/40 px-4 py-3 font-mono text-sm text-cyan-300">
              {done.subdomain}.sahabatpena.id
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Tim kami akan menghubungi <span className="text-slate-300">{done.email}</span> dalam 1x24 jam untuk proses aktivasi & onboarding.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              data-testid="trial-close-btn"
              className="mt-6 h-11 w-full bg-gradient-to-r from-cyan-400 to-violet-600 font-semibold text-[#060A12]"
            >
              Selesai
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Coba Gratis 14 Hari</DialogTitle>
              <DialogDescription className="text-slate-400">
                Daftarkan sekolah Anda. Tanpa kartu kredit, aktif dalam 1x24 jam.
                {presetPackage && <span className="mt-1 block text-cyan-400">Paket dipilih: {presetPackage}</span>}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-2 space-y-4" data-testid="trial-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-300">Nama Sekolah</Label>
                  <Input required value={form.school_name} onChange={(e) => set("school_name", e.target.value)}
                    placeholder="SD Sahabat Bangsa" data-testid="trial-school-name"
                    className="border-cyan-500/20 bg-slate-900/60 text-white placeholder:text-slate-600" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-300">Jenjang</Label>
                  <Select value={form.level} onValueChange={(v) => set("level", v)}>
                    <SelectTrigger data-testid="trial-level" className="border-cyan-500/20 bg-slate-900/60 text-white">
                      <SelectValue placeholder="Pilih jenjang" />
                    </SelectTrigger>
                    <SelectContent className="border-cyan-500/20 bg-[#0D1424] text-white">
                      {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-300">Subdomain Portal</Label>
                <div className="flex items-center overflow-hidden rounded-lg border border-cyan-500/20 bg-slate-900/60 focus-within:border-cyan-400/60">
                  <Input required value={form.subdomain}
                    onChange={(e) => set("subdomain", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="namasekolah" data-testid="trial-subdomain"
                    className="border-0 bg-transparent text-white placeholder:text-slate-600 focus-visible:ring-0 focus-visible:ring-offset-0" />
                  <span className="whitespace-nowrap px-3 font-mono text-xs text-slate-500">.sahabatpena.id</span>
                </div>
                {form.subdomain && (
                  <div className="flex items-center gap-1.5 text-xs" data-testid="trial-subdomain-status">
                    {checking ? (
                      <span className="flex items-center gap-1.5 text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Memeriksa...</span>
                    ) : check?.available ? (
                      <span className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Tersedia!</span>
                    ) : check ? (
                      <span className="flex items-center gap-1.5 text-red-400"><XCircle className="h-3.5 w-3.5" /> {check.reason}</span>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-300">Perkiraan Jumlah Siswa</Label>
                  <Input type="number" min="0" value={form.student_count} onChange={(e) => set("student_count", e.target.value)}
                    placeholder="150" data-testid="trial-student-count"
                    className="border-cyan-500/20 bg-slate-900/60 text-white placeholder:text-slate-600" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-300">Nama Anda (PIC)</Label>
                  <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                    placeholder="Budi Santoso" data-testid="trial-contact-name"
                    className="border-cyan-500/20 bg-slate-900/60 text-white placeholder:text-slate-600" />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-300">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                    placeholder="admin@sekolah.sch.id" data-testid="trial-email"
                    className="border-cyan-500/20 bg-slate-900/60 text-white placeholder:text-slate-600" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-300">No. WhatsApp</Label>
                  <Input required value={form.phone} onChange={(e) => set("phone", e.target.value)}
                    placeholder="0812xxxxxxx" data-testid="trial-phone"
                    className="border-cyan-500/20 bg-slate-900/60 text-white placeholder:text-slate-600" />
                </div>
              </div>

              <Button type="submit" disabled={submitting} data-testid="trial-submit-btn"
                className="h-11 w-full bg-gradient-to-r from-cyan-400 to-violet-600 font-semibold text-[#060A12] hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Kirim & Mulai Uji Coba"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
